import React, { useState, useEffect } from "react";
import { getAuth } from "firebase/auth";
import {
  uploadUserImage,
  getUserImageUrl,
} from "../../firebase/firebaseStorage";
import {
  Box,
  Typography,
  FormLabel,
  useTheme,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  SelectChangeEvent,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import OrbLoader from "../../components/common/OrbLoader";
import { useTranslation } from "react-i18next";
import PageContainer from "../../components/layout/PageContainer";
import { useNavigate, useParams } from "react-router-dom";
import { components } from "../../types/api";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import DeleteIcon from "@mui/icons-material/Delete";
import EmailIcon from "@mui/icons-material/Email";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import { LanguageEnum } from "../../store/slices/app/appTypes";
import {
  useGetUsersQuery,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useRecoverLinkMutation,
} from "../../store/apis/usersApi";
import { useDispatch, useSelector } from "react-redux";
import { setToastMessage } from "../../store/slices/app/appSlice";
import { selectSidebarCollapsed } from "../../store/slices/app/appSelector";
import { setAvatar } from "../../store/slices/session/sessionSlice";
import { selectUser } from "../../store/slices/session/sessionSelector";
import * as Sentry from "@sentry/react";
import PageBreadcrumbs from "../../components/layout/PageBreadcrumbs";
import { useGoBack } from "../../hooks/useGoBack";

type Role = components["schemas"]["_36_Enums.Role"];
type User = components["schemas"]["Omit_User.password_"];

const EditUser: React.FC = () => {
  const collapsed = useSelector(selectSidebarCollapsed);
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const goBack = useGoBack();
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch();

  const { data: usersData } = useGetUsersQuery();
  const [updateUser, { isLoading, data: updatedUser, error: updateError }] =
    useUpdateUserMutation();
  const [deleteUser, { isLoading: isDeleting, error: deleteError }] =
    useDeleteUserMutation();
  const [
    recoverLink,
    { data: recoverLinkData, isLoading: isRecovering, error: recoverError },
  ] = useRecoverLinkMutation();

  const user = usersData?.data?.find((u) => u.id === id);

  const [uploading, setUploading] = useState(false);
  const [imageUrl, setImageUrl] = useState<string>();
  const [isLoadingInitial, setIsLoadingInitial] = useState(true);
  const [recoveryLinkDialogOpen, setRecoveryLinkDialogOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    const fetchImageUrl = async () => {
      setIsLoadingInitial(true);

      if (user?.firebaseUid) {
        try {
          const url = await getUserImageUrl(user.firebaseUid);
          if (url) setImageUrl(url);
        } catch (error) {
          Sentry.captureException(error);
          console.error("Error fetching image URL:", error);
        }
      }
      setIsLoadingInitial(false);
    };
    fetchImageUrl();
  }, [user?.firebaseUid]);

  const [formData, setFormData] = useState<Partial<User>>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    role: "CLIENT" as Role,
    active: true,
    language: undefined,
    themeMode: undefined,
  });

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phone: user.phone,
        role: user.role as Role,
        active: user.active,
        language: user.language,
        themeMode: user.themeMode,
      });
    }
  }, [user]);

  useEffect(() => {
    if (updatedUser) {
      dispatch(
        setToastMessage({
          message: t("users.notifications.updateSuccess"),
          severity: "success",
        })
      );
      navigate("/users");
    }
  }, [updatedUser, dispatch, navigate, t]);

  useEffect(() => {
    if (updateError) {
      dispatch(
        setToastMessage({
          message:
            updateError &&
            "data" in updateError &&
            typeof updateError.data === "object" &&
            updateError.data &&
            "message" in updateError.data
              ? String(updateError.data.message)
              : t("users.notifications.updateError"),
          severity: "error",
        })
      );
    }
  }, [updateError, dispatch, t]);

  useEffect(() => {
    if (recoverLinkData) {
      // Open the dialog to show the recovery link
      setRecoveryLinkDialogOpen(true);

      dispatch(
        setToastMessage({
          message: t("users.notifications.recoverLinkSuccess", {
            email: formData.email,
          }),
          severity: "success",
        })
      );
    }
  }, [recoverLinkData, dispatch, t, formData.email]);

  useEffect(() => {
    if (recoverError) {
      dispatch(
        setToastMessage({
          message:
            recoverError &&
            "data" in recoverError &&
            typeof recoverError.data === "object" &&
            recoverError.data &&
            "message" in recoverError.data
              ? String(recoverError.data.message)
              : t("users.notifications.recoverLinkError"),
          severity: "error",
        })
      );
    }
  }, [recoverError, dispatch, t]);

  useEffect(() => {
    if (deleteError) {
      dispatch(
        setToastMessage({
          message:
            deleteError &&
            "data" in deleteError &&
            typeof deleteError.data === "object" &&
            deleteError.data &&
            "message" in deleteError.data
              ? String(deleteError.data.message)
              : t("common.unknownError"),
          severity: "error",
        })
      );
    }
  }, [deleteError, dispatch, t]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRoleChange = (e: SelectChangeEvent) => {
    setFormData((prev) => ({
      ...prev,
      role: e.target.value as Role,
    }));
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      active: e.target.checked,
    }));
  };

  const sessionUser = useSelector(selectUser);

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!event.target.files || !event.target.files[0]) return;
    if (!user?.firebaseUid) return;

    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    const file = event.target.files[0];
    setUploading(true);

    try {
      // Upload the image and get the URL
      const newImageUrl = await uploadUserImage(file, user.firebaseUid);

      if (newImageUrl) {
        setImageUrl(newImageUrl);

        // If this is the current user's avatar, update Redux with the URL
        if (sessionUser?.uid === user.firebaseUid) {
          dispatch(setAvatar(newImageUrl));
        }

        dispatch(
          setToastMessage({
            message: t("users.notifications.uploadImageSuccess"),
            severity: "success",
          })
        );
      }
    } catch (error) {
      Sentry.captureException(error);
      console.error("Error uploading image:", error);
      dispatch(
        setToastMessage({
          message: t("users.notifications.uploadImageError"),
          severity: "error",
        })
      );
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!user?.id) {
      console.error("No user id available");
      return;
    }
    try {
      await updateUser({
        id: user.id,
        updates: formData,
      }).unwrap();
    } catch (error) {
      Sentry.captureException(error);
      console.error("Edit error:", error);
    }
  };

  return (
    <Box sx={{ display: "flex" }}>
      <Box
        sx={{
          flexGrow: 1,
          px: 4,
          py: 2,

          ...(collapsed && {
            ml: `calc(${theme.spacing(7)} - 15px)`,
          }),
          ...(!collapsed && {
            ml: `calc(${theme.spacing(32)} + 1px)`,
          }),
        }}
      >
        <PageBreadcrumbs />

        <PageContainer
          sx={{ display: "flex", flexDirection: "column", gap: 4 }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography
              variant="h5"
              sx={{
                fontFamily: "Montserrat",
                fontWeight: 700,
                color: theme.palette.grey[900],
              }}
            >
              {t("users.editUser")}
            </Typography>
          </Box>

          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
            sx={{ display: "flex", flexDirection: "column", gap: 4 }}
          >
            <Box sx={{ mb: 2 }}>
              <FormControl>
                <FormLabel
                  sx={{
                    fontFamily: "Nunito",
                    fontWeight: 700,
                    color: theme.palette.grey[900],
                    mb: 1,
                  }}
                >
                  {t("users.profileImage")}
                </FormLabel>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box
                    sx={{
                      width: 120,
                      height: 120,
                      borderRadius: 9999,
                      border: `2px solid ${theme.palette.grey[200]}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                      backgroundColor: theme.palette.grey[100],
                    }}
                  >
                    {uploading || isLoadingInitial ? (
                      <OrbLoader size="medium" />
                    ) : imageUrl ? (
                      <Box
                        component="img"
                        src={imageUrl}
                        alt={user?.firstName || t("users.profileImage")}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <PhotoCamera
                        sx={{ color: theme.palette.grey[400], fontSize: 40 }}
                      />
                    )}
                  </Box>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: "none" }}
                    id="profile-image-upload"
                    disabled={uploading}
                  />
                  <label htmlFor="profile-image-upload">
                    <Button
                      component="span"
                      variant="outlined"
                      startIcon={<PhotoCamera />}
                      disabled={uploading}
                      sx={{
                        borderRadius: 50,
                        textTransform: "none",
                        fontFamily: "Nunito",
                        borderColor: theme.palette.grey[200],
                        color: theme.palette.grey[700],
                        "&:hover": {
                          borderColor: theme.palette.primary.main,
                          backgroundColor: "transparent",
                        },
                      }}
                    >
                      {uploading ? (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <CircularProgress size={16} />
                          {t("loading")}
                        </Box>
                      ) : (
                        t(imageUrl ? "users.changeImage" : "users.uploadImage")
                      )}
                    </Button>
                  </label>
                </Box>
              </FormControl>
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: 4,
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: "Nunito",
                      fontWeight: 700,
                      color: theme.palette.grey[900],
                    }}
                  >
                    {t("users.firstName")}
                  </Typography>
                </Box>
                <TextField
                  required
                  name="firstName"
                  placeholder={t("users.firstName")}
                  value={formData.firstName}
                  onChange={handleInputChange}
                  fullWidth
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 50,
                      fontFamily: "Nunito",
                      fontSize: 16,
                      "& fieldset": {
                        borderColor: theme.palette.grey[200],
                      },
                    },
                  }}
                />
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography
                  sx={{
                    fontFamily: "Nunito",
                    fontWeight: 700,
                    color: theme.palette.grey[900],
                  }}
                >
                  {t("users.lastName")}
                </Typography>
                <TextField
                  required
                  name="lastName"
                  placeholder={t("users.lastName")}
                  value={formData.lastName}
                  onChange={handleInputChange}
                  fullWidth
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 50,
                      fontFamily: "Nunito",
                      fontSize: 16,
                      "& fieldset": {
                        borderColor: theme.palette.grey[200],
                      },
                    },
                  }}
                />
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography
                  sx={{
                    fontFamily: "Nunito",
                    fontWeight: 700,
                    color: theme.palette.grey[900],
                  }}
                >
                  {t("users.email")}
                </Typography>
                <TextField
                  required
                  name="email"
                  type="email"
                  placeholder={t("users.email")}
                  value={formData.email}
                  onChange={handleInputChange}
                  fullWidth
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 50,
                      fontFamily: "Nunito",
                      fontSize: 16,
                      "& fieldset": {
                        borderColor: theme.palette.grey[200],
                      },
                    },
                  }}
                />
              </Box>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: 4,
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography
                  sx={{
                    fontFamily: "Nunito",
                    fontWeight: 700,
                    color: theme.palette.grey[900],
                  }}
                >
                  {t("users.phone")}
                </Typography>
                <TextField
                  name="phone"
                  placeholder={t("users.phone")}
                  value={formData.phone}
                  onChange={handleInputChange}
                  fullWidth
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 50,
                      fontFamily: "Nunito",
                      fontSize: 16,
                      "& fieldset": {
                        borderColor: theme.palette.grey[200],
                      },
                    },
                  }}
                />
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography
                  sx={{
                    fontFamily: "Nunito",
                    fontWeight: 700,
                    color: theme.palette.grey[900],
                  }}
                >
                  {t("users.role")}
                </Typography>
                <FormControl fullWidth>
                  <Select
                    name="role"
                    value={formData.role}
                    onChange={handleRoleChange}
                    displayEmpty
                    sx={{
                      borderRadius: 50,
                      fontFamily: "Nunito",
                      fontSize: 16,
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: theme.palette.grey[200],
                      },
                    }}
                  >
                    <MenuItem value="ADMIN">{t("users.roles.admin")}</MenuItem>
                    <MenuItem value="ACCOUNT">
                      {t("users.roles.account")}
                    </MenuItem>
                    <MenuItem value="CLIENT">
                      {t("users.roles.client")}
                    </MenuItem>
                    <MenuItem value="TECHNICIAN">
                      {t("users.roles.technician", {
                        defaultValue: "Technician",
                      })}
                    </MenuItem>
                    <MenuItem value="PRICING">
                      {t("users.roles.pricing", { defaultValue: "Pricing" })}
                    </MenuItem>
                    <MenuItem value="COMERCIAL">
                      {t("users.roles.comercial", {
                        defaultValue: "Comercial",
                      })}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography
                  sx={{
                    fontFamily: "Nunito",
                    fontWeight: 700,
                    color: theme.palette.grey[900],
                  }}
                >
                  {t("users.status")}
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.active}
                      onChange={handleStatusChange}
                      color="primary"
                    />
                  }
                  label={
                    formData.active
                      ? t("userStatus.active")
                      : t("userStatus.inactive")
                  }
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontFamily: "Nunito",
                      color: theme.palette.grey[900],
                    },
                  }}
                />
              </Box>
            </Box>

            {/* User settings: idioma y tema del usuario editado */}
            <Accordion
              disableGutters
              elevation={0}
              sx={{
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: 4,
                "&:before": { display: "none" },
                "&.Mui-expanded": { borderRadius: 4 },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="user-settings-content"
                id="user-settings-header"
              >
                <Typography
                  sx={{
                    fontFamily: "Nunito",
                    fontWeight: 700,
                    color: theme.palette.grey[900],
                  }}
                >
                  {t("users.userSettings.title")}
                </Typography>
              </AccordionSummary>
              <AccordionDetails
                sx={{ display: "flex", flexDirection: "column", gap: 3 }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontFamily: "Nunito", color: theme.palette.grey[600] }}
                >
                  {t("users.userSettings.description")}
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: 4,
                  }}
                >
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Nunito",
                        fontWeight: 700,
                        color: theme.palette.grey[900],
                      }}
                    >
                      {t("settings.language")}
                    </Typography>
                    <FormControl fullWidth>
                      <Select
                        name="language"
                        value={formData.language ?? ""}
                        onChange={(e: SelectChangeEvent) =>
                          setFormData((prev) => ({
                            ...prev,
                            language: e.target.value,
                          }))
                        }
                        displayEmpty
                        renderValue={(value) =>
                          value
                            ? t(`languages.${value}`)
                            : t("users.userSettings.notSet")
                        }
                        sx={{
                          borderRadius: 50,
                          fontFamily: "Nunito",
                          fontSize: 16,
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: theme.palette.grey[200],
                          },
                        }}
                      >
                        <MenuItem value={LanguageEnum.EN}>
                          {t("languages.en")}
                        </MenuItem>
                        <MenuItem value={LanguageEnum.ES}>
                          {t("languages.es")}
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Nunito",
                        fontWeight: 700,
                        color: theme.palette.grey[900],
                      }}
                    >
                      {t("settings.theme")}
                    </Typography>
                    <ToggleButtonGroup
                      value={formData.themeMode ?? null}
                      exclusive
                      onChange={(_, value) =>
                        value &&
                        setFormData((prev) => ({ ...prev, themeMode: value }))
                      }
                      sx={{
                        "& .MuiToggleButton-root": {
                          color: theme.palette.text.primary,
                          borderColor: theme.palette.grey[200],
                          textTransform: "none",
                          fontFamily: "Nunito",
                          gap: 1,
                          "&.Mui-selected": {
                            backgroundColor: theme.palette.primary.main,
                            color: theme.palette.getContrastText(
                              theme.palette.primary.main
                            ),
                            "&:hover": {
                              backgroundColor: theme.palette.primary.main,
                            },
                          },
                        },
                      }}
                    >
                      <ToggleButton value="light">
                        <LightModeIcon fontSize="small" />
                        {t("settings.lightMode")}
                      </ToggleButton>
                      <ToggleButton value="dark">
                        <DarkModeIcon fontSize="small" />
                        {t("settings.darkMode")}
                      </ToggleButton>
                    </ToggleButtonGroup>
                    {!formData.themeMode && (
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: "Nunito",
                          color: theme.palette.grey[500],
                        }}
                      >
                        {t("users.userSettings.notSet")}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Actions */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
                mt: 2,
              }}
            >
              <Button
                variant="text"
                onClick={() => goBack("/users")}
                startIcon={<KeyboardBackspaceIcon />}
                sx={{
                  color: theme.palette.grey[900],
                  textTransform: "none",
                  fontFamily: "Nunito",
                  fontWeight: 700,
                }}
              >
                {t("common.back")}
              </Button>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<EmailIcon />}
                  onClick={async () => {
                    if (formData.email) {
                      try {
                        await recoverLink(formData.email);
                      } catch (error) {
                        Sentry.captureException(error);
                        console.error("Recover password error:", error);
                      }
                    }
                  }}
                  disabled={isRecovering}
                  sx={{
                    borderRadius: 50,
                    textTransform: "none",
                    fontFamily: "Nunito",
                    fontWeight: 700,
                    px: 3,
                    py: 1.5,
                    borderColor: theme.palette.grey[200],
                    color: theme.palette.grey[900],
                    backgroundColor: theme.palette.background.paper,
                    "&:hover": {
                      borderColor: theme.palette.grey[300],
                      backgroundColor: theme.palette.grey[50],
                    },
                  }}
                >
                  {isRecovering ? (
                    <CircularProgress size={24} />
                  ) : (
                    t("users.recoverLink")
                  )}
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={async () => {
                    if (id) {
                      await deleteUser(id);
                      navigate("/users");
                      dispatch(
                        setToastMessage({
                          message: t("users.notifications.deleteSuccess"),
                          severity: "success",
                        })
                      );
                    }
                  }}
                  startIcon={<DeleteIcon />}
                  disabled={isDeleting}
                  sx={{
                    borderRadius: 50,
                    textTransform: "none",
                    fontFamily: "Nunito",
                    fontWeight: 700,
                    px: 3,
                    py: 1.5,
                    backgroundColor: theme.palette.error.main,
                    color: "white",
                    "&:hover": {
                      backgroundColor: theme.palette.error.dark,
                    },
                  }}
                >
                  {isDeleting ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    t("users.delete")
                  )}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={isLoading}
                  sx={{
                    borderRadius: 50,
                    textTransform: "none",
                    fontFamily: "Nunito",
                    fontWeight: 700,
                    px: 3,
                    py: 1.5,
                    backgroundColor: theme.palette.grey[900],
                    color: theme.palette.getContrastText(
                      theme.palette.grey[900]
                    ),
                    "&:hover": {
                      backgroundColor: theme.palette.grey[800],
                    },
                  }}
                >
                  {isLoading ? (
                    <CircularProgress size={24} />
                  ) : (
                    t("users.updateUser")
                  )}
                </Button>
              </Box>
            </Box>
          </Box>
        </PageContainer>
      </Box>

      {/* Recovery Link Dialog */}
      <Dialog
        open={recoveryLinkDialogOpen}
        onClose={() => {
          setRecoveryLinkDialogOpen(false);
          setLinkCopied(false);
        }}
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 1,
          },
        }}
      >
        <DialogTitle sx={{ fontFamily: "Montserrat", fontWeight: 700 }}>
          {t("users.recoverLink")}
          <IconButton
            aria-label="close"
            onClick={() => {
              setRecoveryLinkDialogOpen(false);
              setLinkCopied(false);
            }}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2, fontFamily: "Nunito" }}>
            {t("users.notifications.recoverLinkInfo", {
              email: formData.email,
            })}
          </Typography>
          <TextField
            fullWidth
            value={recoverLinkData?.data || ""}
            variant="outlined"
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => {
                      if (recoverLinkData?.data) {
                        navigator.clipboard.writeText(recoverLinkData.data);
                        setLinkCopied(true);
                        setTimeout(() => setLinkCopied(false), 2000);
                      }
                    }}
                  >
                    <ContentCopyIcon />
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                fontFamily: "Nunito",
                fontSize: 14,
                "& fieldset": {
                  borderColor: theme.palette.grey[200],
                },
              },
            }}
          />
          {linkCopied && (
            <Typography
              sx={{
                mt: 1,
                color: theme.palette.success.main,
                fontFamily: "Nunito",
              }}
            >
              {t("common.copied")}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRecoveryLinkDialogOpen(false);
              setLinkCopied(false);
            }}
            sx={{
              borderRadius: 50,
              textTransform: "none",
              fontFamily: "Nunito",
              fontWeight: 700,
              px: 3,
              py: 1,
              backgroundColor: theme.palette.grey[900],
              color: theme.palette.getContrastText(theme.palette.grey[900]),
              "&:hover": {
                backgroundColor: theme.palette.grey[800],
              },
            }}
          >
            {t("common.close")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EditUser;
